import React, {
	memo,
	useState,
	useEffect,
	useCallback,
	useRef,
	useMemo,
} from "react";
import { DEFAULT_SHOP_IMG, FETCH_PRODUCT_INFO_URL } from "../../conf";
import { AnimatePresence, motion } from "framer-motion";
import useResource from "../../hooks/useResource";
import { Loader } from "../CoreUI";
import {
	ProductViewActionBar,
	ProductViewActionOverlay,
} from "../productViewComponents/ProductViewCore";
import "./product-view.css";
import anim from "../../utils/anim";

import useTrack, { eventTypes } from "../../hooks/useTrack";
import parseUtm from "../../utils/parseUtm";
import inPreview from "../../utils/inPreview";
import Surface from "../surfaces/Surface";
import ProductViewBasicCard from "../productViewComponents/ProductViewBasicCard";
import ProductViewBrandCard from "../productViewComponents/ProductViewBrandCard";
import ProductViewSimilarCard from "../productViewComponents/ProductViewSimilarCard";
import debounce from "../../utils/debounce";
import useDimensions from "../../hooks/useDimensions";
import usePDP from "../../hooks/usePDP";
import ProductViewInline from "../productViewComponents/ProductViewInline";
import message from "../../utils/message";
import { MESSAGE_TYPES } from "../../hooks/useMessage";
import useData from "../../hooks/useData";

// TODO: REFACTOR EVENT PROPS TO USE NEW brandEventProperty and productEventProperty mappers

const ProductView = memo(({ ...props }) => {
	const { PDPInfo, setPDPInfo, PDPCache, setPDPCache } = usePDP();
	const [productViewData, setProductViewData] = useState(null);
	// const [activeImage, setActiveImage] = useState(null);
	const [showFullCard, setShowFullCard] = useState(true);

	const { innerHeight, offsetTop, inDesktopOnly } = useDimensions();

	const { scrapedInformation } = useData();

	const purchases = useMemo(() => {
		let { purchases } = scrapedInformation;
		try {
			purchases = JSON.parse(purchases);
		} catch (_) {
			purchases = null;
		}
		return purchases;
	}, [scrapedInformation]);

	const [{ data, error, loading }, fetchProductInformation, resetData] =
		useResource(
			{
				url: FETCH_PRODUCT_INFO_URL,
				method: "POST",
				data: {
					// product_id: "4984d4e0-f598-4de9-a2b7-a9c7e16eebe2", // has variants
					// product_id: "22c955dd-0f7b-4420-bbb8-5691c0228bc5",
					// product_id: "8aab1a8f-ea39-4488-b16e-d0e3fc3c67be", // has similar
					product_id: PDPInfo?.remote_id || "",
					purchases: purchases || [],
				},
			},
			false
		);

	const track = useTrack();
	const [firedOpen, setFiredOpen] = useState(false);

	const [selectedVariant, setSelectedVariant] = useState({});
	const [quantity, setQuantity] = useState(1);
	const [purchaseError, setPurchaseError] = useState(false);

	const [showActionOverlay, setShowActionOverlay] = useState(false);

	const [showActionBarLoader, setShowActionBarLoader] = useState(false);

	const rootRef = useRef(null);

	const productViewEventProps = useMemo(() => {
		if (
			!productViewData ||
			!productViewData.product ||
			!productViewData.brand
		) {
			return {};
		}
		const { product, brand } = productViewData;
		const {
			remote_id,
			price,
			discounted_price,
			brand_id,
			name,
			id,
			variants,
		} = product;
		const retValue = {
			product: name,
			product_id: id || "",
			product_remote_id: remote_id,
			brand: brand.name,
			price: price || "",
			discounted_price: discounted_price || "",
			brand_id,
			variants: JSON.stringify(variants),
		};
		return retValue;
	}, [productViewData]);

	const {
		product,
		brand,
		similar_products: similarProducts,
	} = useMemo(() => productViewData || {}, [productViewData]);

	const isSingleVariant = useMemo(() => {
		if (!productViewData?.product?.variants) {
			return null;
		}
		return productViewData.product.variants.single_variant || false;
	}, [productViewData]);

	const onPDPClose = useCallback(() => {
		setProductViewData(null);
		track(eventTypes.WIDGET_PDP_CLOSE, {
			...productViewEventProps,
		});
	}, [track, productViewEventProps]);

	const handlePDPClose = useCallback(() => {
		onPDPClose();
		setPDPInfo(false);
		if (!inDesktopOnly) {
			// FIRE CONDITIONALLY IF ALREADY IN MIN MODE
			message(MESSAGE_TYPES.MODE_INLINE);
		}
	}, [setPDPInfo, inDesktopOnly, onPDPClose]);

	const navToTop = useCallback(() => {
		if (!rootRef || !rootRef.current) {
			return;
		}
		rootRef.current.scrollTop = 0;
	}, []);

	const handleProductViewData = useCallback((callback) => {
		setProductViewData((productViewData) => callback(productViewData));
	}, []);

	const handleActionOverlayOpen = useCallback(() => {
		setShowActionOverlay(true);
	}, []);
	const handleActionOverlayClose = useCallback(() => {
		track(eventTypes.WIDGET_PDP_VARIANT_SCREEN_CLOSE, {
			...productViewEventProps,
		});
		setShowActionOverlay(false);
	}, [productViewEventProps, track]);

	const handlePurchase = useCallback(() => {
		if (inPreview) {
			return;
		}
		const baseUrl = productViewData.brand.shopify_shop;
		if (!baseUrl || !selectedVariant.variant_id) {
			setPurchaseError(
				"Whoops! We are unable to purchase this product at the moment. Please try again later"
			);
			return setShowActionOverlay(false);
		}
		track(eventTypes.WIDGET_PDP_CHECKOUT_CLICK, {
			...productViewEventProps,
			display_variant_screen: showActionOverlay,
			selected_variant: selectedVariant.variant_id,
			quantity,
		});
		const url = `${baseUrl}/cart/${selectedVariant.variant_id}:${quantity}`;
		setShowActionBarLoader(true);
		if (window.parent) {
			return (window.parent.location.href = url);
		}
		return (window.location.href = url);
	}, [
		productViewData,
		quantity,
		selectedVariant,
		productViewEventProps,
		showActionOverlay,
		track,
	]);

	// @ARYAN -  verify this!
	const handleScroll = useMemo(
		() =>
			debounce(() => {
				if (!rootRef?.current) {
					return;
				}
				const scroll = rootRef.current.scrollTop;
				if (scroll > 10) {
					setShowFullCard(false);
				} else {
					setShowFullCard(true);
				}
			}, 20),
		[]
	);

	const navBar = useMemo(
		() => ({
			props: {
				product,
				useArrowForClose: true,
				className: "product-view-nav-bar",
			},
			hidden: loading,
		}),
		[product, loading]
	);

	const actionBar = useMemo(
		() => ({
			children: (
				<ProductViewActionBar
					actionOverlay={showActionOverlay}
					handleActionOverlay={handleActionOverlayOpen}
					isSingleVariant={isSingleVariant}
					handlePurchase={handlePurchase}
					quantity={quantity}
					setQuantity={setQuantity}
					maxQuantity={product?.max_quantity || 5}
					loading={showActionBarLoader}
					productViewEventProps={productViewEventProps}
				/>
			),
		}),
		[
			showActionOverlay,
			handleActionOverlayOpen,
			isSingleVariant,
			handlePurchase,
			quantity,
			setQuantity,
			product,
			showActionBarLoader,
			productViewEventProps,
		]
	);

	useEffect(() => {
		if (!PDPInfo) {
			if (inDesktopOnly && productViewData) {
				onPDPClose();
			}
			setFiredOpen(false);
			return;
		}
		if (PDPInfo.remote_id in PDPCache) {
			setProductViewData(PDPCache[PDPInfo.remote_id]);
			return;
		}
		// fetchProductInformation();
		setPDPCache((PDPCache) => ({
			...PDPCache,
			[PDPInfo.remote_id]: PDPInfo,
		}));
		setProductViewData(PDPInfo);
	}, [
		PDPInfo,
		PDPCache,
		fetchProductInformation,
		inDesktopOnly,
		productViewData,
		onPDPClose,
		setPDPCache,
	]);

	useEffect(() => {
		if (!error) {
			return;
		}
		let { masked_url: url, utmFlags, overrideUtm } = PDPInfo;

		if (utmFlags && overrideUtm) {
			url = parseUtm(url, utmFlags, overrideUtm);
		}

		const productWindow = window.open();
		if (productWindow) {
			productWindow.opener = null;
			productWindow.location = url;
		}

		handlePDPClose();
	}, [error, setPDPInfo, PDPInfo, handlePDPClose]);

	useEffect(() => {
		if (!data) {
			return;
		}
		setPDPCache((PDPCache) => ({
			...PDPCache,
			[PDPInfo.remote_id]: data,
		}));
		setProductViewData(data);
		resetData();
	}, [data, setPDPCache, PDPInfo, resetData]);

	useEffect(() => {
		if (!isSingleVariant) {
			return;
		}
		setSelectedVariant({
			variant_id: productViewData.product.variants.variant_id,
		});
	}, [isSingleVariant, productViewData]);

	useEffect(() => {
		if (firedOpen || !PDPInfo) {
			return;
		}
		const { remote_id, brand, price_str, discounted_price_str, name, id } =
			PDPInfo;

		track(eventTypes.WIDGET_PDP_OPEN, {
			product: name,
			product_id: id,
			product_remote_id: remote_id,
			brand: brand,
			price: price_str || "",
			discounted_price: discounted_price_str || "",
		});
		setFiredOpen(true);
	}, [firedOpen, track, PDPInfo]);

	useEffect(() => {
		if (!productViewData || !productViewData.product) {
			return;
		}
		track(eventTypes.WIDGET_PDP_DISPLAY, {
			...productViewEventProps,
		});
	}, [track, productViewData, productViewEventProps]);

	return (
		<AnimatePresence>
			{PDPInfo && PDPInfo.remote_id && (
				<>
					{inDesktopOnly ? (
						<ProductViewInline
							loading={loading}
							product={product}
							similarProducts={similarProducts}
							brand={brand}
							handleClose={handlePDPClose}
							// action overlay specific
							showActionOverlay={showActionOverlay}
							handleActionOverlayOpen={handleActionOverlayOpen}
							handleActionOverlayClose={handleActionOverlayClose}
							selectedVariant={selectedVariant}
							setSelectedVariant={setSelectedVariant}
							error={purchaseError}
							setError={setPurchaseError}
							// common to action bar + action overlay
							isSingleVariant={isSingleVariant}
							handlePurchase={handlePurchase}
							quantity={quantity}
							setQuantity={setQuantity}
							maxQuantity={product?.max_quantity || 5}
							actionBarLoading={showActionBarLoader}
							productViewEventProps={productViewEventProps}
							{...props}
						/>
					) : (
						<motion.section
							className="surface-orchestrator-container"
							style={{
								top: `${offsetTop}px`,
								height: `${innerHeight}px`,
							}}
							ref={rootRef}
							onScroll={handleScroll}
							variants={anim.pdp}
							initial="initial"
							animate="animate"
							exit="exit"
						>
							{loading && (
								<Loader
									className="product-view-loader"
									center
								/>
							)}
							{productViewData && product && (
								<Surface
									handleClose={handlePDPClose}
									imageView={{
										// direct: true,
										imgArray: product?.supplementary_images,
										imgUrl:
											product?.photo_url ||
											DEFAULT_SHOP_IMG,
										enableDrag: true,
										dragBreakPoint: 200,
										onDragComplete: handlePDPClose,
									}}
									showSurfaceTab={false}
									actionBar={actionBar}
									navBar={navBar}
								>
									<ProductViewBasicCard
										product={product}
										brand={brand}
										error={purchaseError}
										showFullCard={showFullCard}
										productViewEventProps={
											productViewEventProps
										}
									/>
									<ProductViewBrandCard
										brand={brand}
										productViewEventProps={
											productViewEventProps
										}
									/>
									<ProductViewSimilarCard
										product={product}
										similarProducts={similarProducts}
										setProductViewData={
											handleProductViewData
										}
										navToTop={navToTop}
										productViewEventProps={
											productViewEventProps
										}
									/>
									<ProductViewActionOverlay
										open={showActionOverlay}
										product={product}
										selectedVariant={selectedVariant}
										setSelectedVariant={setSelectedVariant}
										quantity={quantity}
										error={purchaseError}
										setError={setPurchaseError}
										handleClose={handleActionOverlayClose}
									/>
								</Surface>
							)}
						</motion.section>
					)}
				</>
			)}
		</AnimatePresence>
	);
});

export default ProductView;
